import type { SalesPolicyBody, SalesStageSettingsEntry } from '../../../../api/client';

/** The rich, editor-friendly policy shape used by the ported salesB.jsx
 * components. We keep it as an internal draft model and map it to/from the real
 * SalesPolicyBody at the API boundary so the design components stay verbatim. */
export type OnMax = SalesPolicyBody['on_max_negotiations'];

export interface SStage {
  id: string;
  name: string;
  kind: 'free' | 'sale';
  price: number;
  floor: number;
  guidance: string;
  // Offer framing (ADR 0056): how the AI WORDS this paid reveal to the fan — a
  // different axis from `guidance` (which media to pick). Sale stages only;
  // '' -> the built-in code default. Persisted only when non-blank.
  offerFraming: string;
  // Deterministic content limits (null = unset -> AI decides). When set,
  // bigdayz.new keeps each PPV within these photo/video counts.
  photosMin: number | null;
  photosMax: number | null;
  videosMin: number | null;
  videosMax: number | null;
}

export interface SNegotiation {
  maxAttempts: number;
  onMax: OnMax;
  absMinPrice: number | null;
  graceTurns: number;
  maxTurns: number;
  continueAfterDecline: boolean;
  prompt: string;
  notifyHardDecline: boolean;
  notifyBusy: boolean;
  notifyDisengage: boolean;
}

export interface SPolicy {
  maxStages: number;
  restrictToOneVault: boolean;
  stages: SStage[];
  notifyLadderComplete: boolean;
  strategyPrompt: string;
  negotiation: SNegotiation;
}

export const ON_MAX_OPTIONS: { id: OnMax; label: string; desc: string }[] = [
  { id: 'exit_to_communication', label: 'Exit to communication', desc: 'Drop the sale and return to normal chat.' },
  { id: 'adjust_price_to_floor', label: 'Adjust to floor price', desc: 'Drop to the absolute minimum price.' },
  { id: 'notify', label: 'Notify', desc: 'Stop and notify a human chatter.' },
];

export const MAX_SALE_STAGES = 10;

export const MAX_TEASING_STAGES = 5;

/** PPV prices must be free ($0) or at least this amount. */
export const MIN_PAID_PRICE = 3;

export function fmt(n: number): string {
  return '$' + Number(n || 0).toFixed(2).replace(/\.00$/, '');
}

/** Returns a human-readable error if any paid price is below the $3 floor,
 * else null. A price of 0 (free) is always allowed. */
export function validatePolicy(s: SPolicy): string | null {
  const tooLow = (n: number | null | undefined) =>
    n != null && n > 0 && n < MIN_PAID_PRICE;
  for (const st of saleStages(s)) {
    if (tooLow(st.price) || tooLow(st.floor)) {
      return `${st.name}: prices must be ${fmt(MIN_PAID_PRICE)} or more (or $0 for free).`;
    }
  }
  // Content limits: max must be >= min for each kind (mirrors the backend).
  const badRange = (lo: number | null, hi: number | null) => lo != null && hi != null && hi < lo;
  for (const st of s.stages) {
    if (badRange(st.photosMin, st.photosMax)) return `${st.name}: max photos must be ≥ min photos.`;
    if (badRange(st.videosMin, st.videosMax)) return `${st.name}: max videos must be ≥ min videos.`;
  }
  if (tooLow(s.negotiation.absMinPrice)) {
    return `Absolute minimum price must be ${fmt(MIN_PAID_PRICE)} or more (or $0).`;
  }
  return null;
}

export function saleStages(s: SPolicy): SStage[] {
  return s.stages.filter((x) => x.kind === 'sale');
}

export function teasingStages(s: SPolicy): SStage[] {
  return s.stages.filter((x) => x.kind === 'free');
}

export function priceRange(s: SPolicy): string {
  const ss = saleStages(s);
  if (!ss.length) return '—';
  return fmt(ss[0].price) + ' → ' + fmt(ss[ss.length - 1].price);
}

export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const suffixes = ['th', 'st', 'nd', 'rd'];
  return `${n}${suffixes[n % 10] ?? 'th'}`;
}

/** Stage key used by the backend's sales_stages_settings map (e.g. "1st sale"). */
export function stageKey(i: number): string {
  return `${ordinal(i)} sale`;
}

/** Teasing stage key: bare "teasing" when there's only one (matches the
 * existing single-stage convention), "1st teasing"/"2nd teasing"/... once the
 * policy has more than one — mirrors how sale stages are labeled. */
export function teasingStageKey(i: number, total: number): string {
  return total > 1 ? `${ordinal(i)} teasing` : 'teasing';
}

interface PolicyChip {
  icon: string;
  text: string;
}

export function policyChips(s: SPolicy): PolicyChip[] {
  const ss = saleStages(s);
  const ts = teasingStages(s);
  const onMax = ON_MAX_OPTIONS.find((o) => o.id === s.negotiation.onMax);
  return [
    { icon: 'layers', text: ss.length + ' sale stage' + (ss.length === 1 ? '' : 's') },
    { icon: 'dollar', text: priceRange(s) },
    { icon: 'lock', text: 'floor from ' + fmt(ss.length ? ss[0].floor : 0) },
    { icon: 'sliders', text: 'haggle ' + s.negotiation.maxAttempts + '× · ' + (onMax ? onMax.label.split(' ')[0].toLowerCase() : '—') },
    { icon: 'gift', text: ts.length + ' free teaser' + (ts.length === 1 ? '' : 's') },
  ];
}

const DEFAULT_SALE_STAGES = 10;

/** A stage entry is teasing-type purely by convention: price === 0. Matches
 * the backend's sales_stages_settings docstring (database/sales_ladder_policy_db.py). */
function isTeasingEntry(entry: SalesStageSettingsEntry | undefined): boolean {
  return (entry?.price ?? 0) === 0;
}

/** Number of sale stages encoded in a settings map (mirrors the modal's logic). */
export function countSaleStages(settings: SalesPolicyBody['sales_stages_settings']): number {
  if (!settings) return DEFAULT_SALE_STAGES;
  const saleKeys = Object.values(settings).filter((v) => !isTeasingEntry(v));
  return saleKeys.length > 0 ? saleKeys.length : DEFAULT_SALE_STAGES;
}

/** Number of teasing (price=0) stages encoded in a settings map. Always at
 * least 1 — a policy always has a free warm-up stage. */
export function countTeasingStages(settings: SalesPolicyBody['sales_stages_settings']): number {
  if (!settings) return 1;
  const teasingKeys = Object.values(settings).filter(isTeasingEntry);
  return teasingKeys.length > 0 ? teasingKeys.length : 1;
}

/** Read a stage entry's optional photo/video ranges into the draft shape. */
function readComposition(entry: SalesStageSettingsEntry | undefined): Pick<
  SStage, 'photosMin' | 'photosMax' | 'videosMin' | 'videosMax'
> {
  const num = (v: number | undefined) => (typeof v === 'number' ? v : null);
  return {
    photosMin: num(entry?.photos_min),
    photosMax: num(entry?.photos_max),
    videosMin: num(entry?.videos_min),
    videosMax: num(entry?.videos_max),
  };
}

interface DraftEntry { kind: 'free' | 'sale'; entry: SalesStageSettingsEntry | undefined }

/** SalesPolicyBody → editor draft model.
 *
 * sales_stages_settings is JSONB, whose object key order Postgres does NOT
 * preserve — so sequence is read from each entry's explicit `order` field
 * (written by the reorderable stage list below) when ANY entry carries one.
 * A policy saved before `order` existed has it on no entry; that falls back
 * to the historical convention — every teasing stage before every sale stage
 * — so it keeps reading exactly as it did before this field existed (must
 * match bigdayz.new's `_stage_sort_key` fallback exactly). */
export function bodyToPolicy(body: SalesPolicyBody): SPolicy {
  const settings = body.sales_stages_settings ?? {};
  const count = countSaleStages(body.sales_stages_settings);
  const teasingCount = countTeasingStages(body.sales_stages_settings);

  const teasing: DraftEntry[] = [];
  for (let i = 1; i <= teasingCount; i++) {
    const key = teasingStageKey(i, teasingCount);
    // Fall back to the bare "teasing" key for the first stage, so policies
    // that predate pluralization (or were only ever configured with one
    // teasing stage under a non-canonical name) still read correctly.
    const entry = settings[key] ?? (i === 1 ? settings['teasing'] : undefined);
    teasing.push({ kind: 'free', entry });
  }
  const sale: DraftEntry[] = [];
  for (let i = 1; i <= count; i++) {
    sale.push({ kind: 'sale', entry: settings[stageKey(i)] });
  }

  const all = [...teasing, ...sale];
  if (all.some((d) => typeof d.entry?.order === 'number')) {
    all.sort((a, b) => (a.entry?.order ?? Number.MAX_SAFE_INTEGER) - (b.entry?.order ?? Number.MAX_SAFE_INTEGER));
  }

  let saleI = 0;
  let teaseI = 0;
  const stages: SStage[] = all.map((d) => {
    if (d.kind === 'free') {
      teaseI += 1;
      return {
        id: 't' + teaseI,
        name: teasingCount > 1 ? ordinal(teaseI) + ' Teasing' : 'Teasing',
        kind: 'free',
        price: 0,
        floor: 0,
        guidance: d.entry?.description ?? '',
        offerFraming: '',
        ...readComposition(d.entry),
      };
    }
    saleI += 1;
    return {
      id: 's' + saleI,
      name: ordinal(saleI) + ' Sale',
      kind: 'sale',
      price: d.entry?.price ?? 0,
      floor: d.entry?.min_price ?? 0,
      guidance: d.entry?.description ?? '',
      offerFraming: d.entry?.offer_framing ?? '',
      ...readComposition(d.entry),
    };
  });
  return {
    maxStages: count,
    restrictToOneVault: body.restrict_to_one_vault,
    stages,
    notifyLadderComplete: body.notify_on_completion,
    strategyPrompt: body.sales_strategy_prompt ?? '',
    negotiation: {
      maxAttempts: body.max_negotiation_attempts,
      onMax: body.on_max_negotiations,
      absMinPrice: body.absolute_min_price,
      graceTurns: body.negotiation_grace_turns,
      maxTurns: body.max_negotiation_turns,
      continueAfterDecline: body.continue_sales_on_decline,
      prompt: body.negotiation_prompt ?? '',
      notifyHardDecline: body.notify_on_decline,
      notifyBusy: body.notify_when_fan_busy,
      notifyDisengage: body.notify_when_fan_disengaged,
    },
  };
}

/** Write a stage's set (non-null) photo/video ranges onto its settings entry. */
function writeComposition(st: SStage): Partial<SalesStageSettingsEntry> {
  const out: Partial<SalesStageSettingsEntry> = {};
  if (st.photosMin != null) out.photos_min = st.photosMin;
  if (st.photosMax != null) out.photos_max = st.photosMax;
  if (st.videosMin != null) out.videos_min = st.videosMin;
  if (st.videosMax != null) out.videos_max = st.videosMax;
  return out;
}

/** Editor draft model → SalesPolicyBody (preserving any unmapped fields).
 *
 * Writes stages in `s.stages`' ACTUAL array order (teasing and sale may be
 * interleaved) and stamps an explicit `order` = array position on every
 * entry, so a round-trip through this UI always leaves a policy fully
 * order-explicit — no lingering ambiguity for a later read (by this UI or by
 * bigdayz.new's initiation prompt) to fall back on JSONB's unreliable key
 * order. */
export function policyToBody(s: SPolicy, prev: SalesPolicyBody): SalesPolicyBody {
  const settings: Record<string, SalesStageSettingsEntry> = {};
  const teaserTotal = teasingStages(s).length;
  let saleI = 0;
  let teaseI = 0;
  s.stages.forEach((st, idx) => {
    if (st.kind === 'free') {
      teaseI += 1;
      settings[teasingStageKey(teaseI, teaserTotal)] = {
        description: st.guidance ?? '', price: 0, min_price: 0, order: idx + 1,
        ...writeComposition(st),
      };
    } else {
      saleI += 1;
      settings[stageKey(saleI)] = {
        description: st.guidance ?? '',
        price: st.price || 0,
        min_price: st.floor || 0,
        order: idx + 1,
        // Offer framing (ADR 0056), sale stages only. Persist only when non-blank
        // so an unedited stage stays absent and falls to the code default.
        ...(st.offerFraming && st.offerFraming.trim() ? { offer_framing: st.offerFraming.trim() } : {}),
        ...writeComposition(st),
      };
    }
  });
  const n = s.negotiation;
  return {
    ...prev,
    is_active: true,
    restrict_to_one_vault: s.restrictToOneVault,
    sales_stages_settings: settings,
    absolute_min_price: n.absMinPrice,
    max_negotiation_attempts: n.maxAttempts,
    on_max_negotiations: n.onMax,
    negotiation_grace_turns: n.graceTurns,
    max_negotiation_turns: n.maxTurns,
    negotiation_prompt: n.prompt,
    sales_strategy_prompt: s.strategyPrompt,
    notify_on_completion: s.notifyLadderComplete,
    notify_on_decline: n.notifyHardDecline,
    continue_sales_on_decline: n.continueAfterDecline,
    notify_when_fan_busy: n.notifyBusy,
    notify_when_fan_disengaged: n.notifyDisengage,
  };
}

/** Default sales ladder for a fresh account: free teaser + three paid stages. */
export const DEFAULT_STAGES_SETTINGS: NonNullable<SalesPolicyBody['sales_stages_settings']> = {
  teasing: {
    description: 'Send 1-2 non-nude or mildly suggestive photos as a spontaneous free gift. Flirty, not explicit — this is the hook.',
    price: 0,
    min_price: 0,
  },
  '1st sale': {
    description: 'Bundle of 3-5 nude photos (boobs only) with 1-2 short non-explicit videos. First paid reveal — soft and promising.',
    price: 9.99,
    min_price: 4.99,
  },
  '2nd sale': {
    description: 'Single video 1-3 minutes. Undressing, stripping, or teasing clip. Lower price tier — appetizer, not the main event.',
    price: 19.99,
    min_price: 11.99,
  },
  '3rd sale': {
    description: 'Single video 2-4 minutes. Escalation: continuing undress, close-up teasing, or blowjob intro.',
    price: 29.99,
    min_price: 17.99,
  },
};

export const POLICY_DEFAULTS: SalesPolicyBody = {
  is_active: true,
  restrict_to_one_vault: false,
  sales_stages_settings: DEFAULT_STAGES_SETTINGS,
  absolute_min_price: null,
  max_negotiation_attempts: 3,
  on_max_negotiations: 'exit_to_communication',
  negotiation_grace_turns: 2,
  max_negotiation_turns: 6,
  negotiation_prompt: '',
  sales_strategy_prompt: '',
  notify_on_completion: true,
  notify_on_decline: true,
  continue_sales_on_decline: false,
  notify_when_fan_busy: true,
  notify_when_fan_disengaged: true,
};
